import React, { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { Download, FileSpreadsheet, FileText, File, Loader2, Lightbulb, BarChart3, Image } from 'lucide-react';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table, TableRow, TableCell, WidthType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';

/**
 * Insights Export Component
 * Exports AI insights/recommendations to CSV, PDF, or Word
 * Now supports chart export via html2canvas
 * 
 * @param {Array} insights - Array of insight objects with title, description, severity, etc.
 * @param {string} pageTitle - Title of the page/section for the export header
 * @param {string} variant - Button variant (default, outline, ghost)
 * @param {string} size - Button size (default, sm, lg)
 * @param {Array} chartRefs - Array of refs to chart containers to include in PDF
 * @param {Array} chartSelectors - Array of CSS selectors to find charts on the page
 */
export function InsightsExport({ 
  insights = [], 
  pageTitle = "Insights", 
  variant = "outline", 
  size = "sm",
  chartRefs = [],
  chartSelectors = []
}) {
  const [downloading, setDownloading] = useState(null);

  const formatDate = () => new Date().toISOString().split('T')[0];
  const formatDateTime = () => new Date().toLocaleString();

  const exportCSV = () => {
    setDownloading('csv');
    try {
      if (!insights || insights.length === 0) {
        toast.error('No insights to export');
        setDownloading(null);
        return;
      }

      // CSV headers
      const headers = ['Title', 'Description', 'Severity', 'Category', 'Impact', 'Recommendation', 'Created At'];
      
      // Build CSV rows
      const rows = insights.map(insight => [
        `"${(insight.title || insight.name || '').replace(/"/g, '""')}"`,
        `"${(insight.description || insight.message || insight.content || '').replace(/"/g, '""')}"`,
        insight.severity || insight.priority || insight.level || 'info',
        insight.category || insight.type || 'General',
        insight.impact || insight.value || '',
        `"${(insight.recommendation || insight.action || '').replace(/"/g, '""')}"`,
        insight.created_at || insight.timestamp || formatDateTime()
      ].join(','));

      const csvContent = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const filename = `${pageTitle.toLowerCase().replace(/\s+/g, '_')}_insights_${formatDate()}.csv`;
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();

      toast.success(`Exported ${insights.length} insights to CSV`);
    } catch (error) {
      console.error('CSV export error:', error);
      toast.error('Failed to export CSV');
    } finally {
      setDownloading(null);
    }
  };

  // Capture charts from the page
  const captureCharts = async () => {
    const charts = [];
    
    // From refs
    for (const ref of chartRefs) {
      if (ref?.current) {
        try {
          const canvas = await html2canvas(ref.current, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff'
          });
          charts.push(canvas.toDataURL('image/png'));
        } catch (e) {
          console.warn('Failed to capture chart from ref:', e);
        }
      }
    }
    
    // From selectors (find Recharts containers, custom charts, etc.)
    const defaultSelectors = [
      '.recharts-wrapper',
      '[data-chart]',
      '.chart-container',
      ...chartSelectors
    ];
    
    for (const selector of defaultSelectors) {
      const elements = document.querySelectorAll(selector);
      for (const element of elements) {
        // Check if element is visible and has dimensions
        const rect = element.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          try {
            const canvas = await html2canvas(element, {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              backgroundColor: '#ffffff',
              logging: false
            });
            charts.push(canvas.toDataURL('image/png'));
          } catch (e) {
            console.warn('Failed to capture chart:', e);
          }
        }
      }
    }
    
    return charts;
  };

  const exportPDF = async () => {
    setDownloading('pdf');
    try {
      if (!insights || insights.length === 0) {
        toast.error('No insights to export');
        setDownloading(null);
        return;
      }

      const pdf = new jsPDF();
      const margin = 20;
      let y = margin;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const maxWidth = pageWidth - (margin * 2);

      // Capture charts first (show loading state)
      toast.info('Capturing charts...', { duration: 2000 });
      const chartImages = await captureCharts();

      // Load and add logo
      try {
        const logoImg = new window.Image();
        logoImg.crossOrigin = 'anonymous';
        logoImg.src = '/goodwin-hospice-logo.png';
        
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          setTimeout(reject, 3000); // Timeout after 3s
        });

        // Add logo to header (right side)
        const logoWidth = 35;
        const logoHeight = (logoImg.height / logoImg.width) * logoWidth;
        
        // Create canvas to convert logo
        const canvas = document.createElement('canvas');
        canvas.width = logoImg.width;
        canvas.height = logoImg.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(logoImg, 0, 0);
        const logoData = canvas.toDataURL('image/png');
        
        pdf.addImage(logoData, 'PNG', pageWidth - margin - logoWidth, margin - 5, logoWidth, logoHeight);
      } catch (logoError) {
        console.warn('Could not load logo:', logoError);
      }

      // Title (with space for logo on right)
      pdf.setFontSize(20);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(13, 148, 136); // Teal color
      pdf.text(`${pageTitle}`, margin, y);
      y += 8;
      
      // Subtitle
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text('Insights Report with Analytics', margin, y);
      y += 6;
      
      // Organization name
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(64, 64, 64);
      pdf.text('Goodwin Hospice', margin, y);
      y += 8;

      // Subtitle with date
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(128, 128, 128);
      pdf.text(`Generated: ${formatDateTime()}`, margin, y);
      y += 15;

      // Add charts section if charts were captured
      if (chartImages.length > 0) {
        pdf.setFontSize(14);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text('Visual Analytics', margin, y);
        y += 10;

        for (let i = 0; i < chartImages.length; i++) {
          const imgData = chartImages[i];
          
          // Calculate image dimensions to fit page
          const img = new window.Image();
          img.src = imgData;
          
          // Wait for image to load to get dimensions
          await new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });

          const imgWidth = Math.min(maxWidth, 170);
          const imgHeight = (img.height / img.width) * imgWidth;
          
          // Check if we need a new page
          if (y + imgHeight > pageHeight - 30) {
            pdf.addPage();
            y = margin;
          }
          
          // Add chart image
          pdf.addImage(imgData, 'PNG', margin, y, imgWidth, imgHeight);
          y += imgHeight + 10;
          
          // Add chart label
          pdf.setFontSize(8);
          pdf.setFont('helvetica', 'italic');
          pdf.setTextColor(128, 128, 128);
          pdf.text(`Chart ${i + 1}`, margin, y);
          y += 15;
        }

        // Add separator line
        pdf.setDrawColor(200, 200, 200);
        pdf.line(margin, y, pageWidth - margin, y);
        y += 15;
      }

      // Insights section header
      pdf.setFontSize(14);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(30, 41, 59);
      pdf.text('Key Insights & Recommendations', margin, y);
      y += 12;

      // Insights
      insights.forEach((insight, index) => {
        // Check if we need a new page
        if (y > pageHeight - 50) {
          pdf.addPage();
          y = margin;
        }

        // Insight box background
        const boxHeight = 35;
        const severity = (insight.severity || insight.priority || 'info').toUpperCase();
        const bgColor = severity === 'CRITICAL' ? [254, 242, 242] : 
                       severity === 'HIGH' || severity === 'WARNING' ? [255, 251, 235] : 
                       severity === 'POSITIVE' ? [240, 253, 244] : [248, 250, 252];
        const borderColor = severity === 'CRITICAL' ? [239, 68, 68] : 
                           severity === 'HIGH' || severity === 'WARNING' ? [245, 158, 11] : 
                           severity === 'POSITIVE' ? [34, 197, 94] : [148, 163, 184];
        
        // Draw insight box
        pdf.setFillColor(...bgColor);
        pdf.setDrawColor(...borderColor);
        pdf.roundedRect(margin, y - 5, maxWidth, boxHeight + 10, 2, 2, 'FD');

        // Severity badge
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...borderColor);
        pdf.text(`${severity}`, margin + 5, y + 3);

        // Title
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        const title = insight.title || insight.name || `Insight ${index + 1}`;
        pdf.text(title, margin + 5, y + 12);

        // Description (truncated)
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(71, 85, 105);
        const description = insight.description || insight.message || insight.content || '';
        if (description) {
          const truncatedDesc = description.length > 200 ? description.substring(0, 200) + '...' : description;
          const lines = pdf.splitTextToSize(truncatedDesc, maxWidth - 10);
          lines.slice(0, 2).forEach((line, lineIndex) => {
            pdf.text(line, margin + 5, y + 20 + (lineIndex * 4));
          });
        }

        // Recommendation
        const recommendation = insight.recommendation || insight.action;
        if (recommendation) {
          pdf.setFont('helvetica', 'italic');
          pdf.setTextColor(13, 148, 136);
          const recText = `→ ${recommendation.substring(0, 80)}${recommendation.length > 80 ? '...' : ''}`;
          pdf.text(recText, margin + 5, y + boxHeight + 2);
        }

        y += boxHeight + 20;
      });

      // Summary section
      if (y < pageHeight - 60) {
        y += 10;
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(30, 41, 59);
        pdf.text('Summary', margin, y);
        y += 8;
        
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(71, 85, 105);
        
        const criticalCount = insights.filter(i => (i.severity || '').toLowerCase() === 'critical').length;
        const warningCount = insights.filter(i => ['warning', 'high'].includes((i.severity || '').toLowerCase())).length;
        const positiveCount = insights.filter(i => (i.severity || '').toLowerCase() === 'positive').length;
        
        pdf.text(`Total Insights: ${insights.length}`, margin, y);
        y += 5;
        if (criticalCount > 0) pdf.text(`Critical Issues: ${criticalCount}`, margin, y); y += 5;
        if (warningCount > 0) pdf.text(`Warnings: ${warningCount}`, margin, y); y += 5;
        if (positiveCount > 0) pdf.text(`Positive Indicators: ${positiveCount}`, margin, y);
        if (chartImages.length > 0) {
          y += 5;
          pdf.text(`Charts Included: ${chartImages.length}`, margin, y);
        }
      }

      // Footer on all pages with logo
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(128, 128, 128);
        pdf.text('Goodwin Hospice - Confidential', margin, pageHeight - 10);
        pdf.text(`Page ${i} of ${pageCount}`, pageWidth - margin - 25, pageHeight - 10);
      }

      const filename = `goodwin_hospice_${pageTitle.toLowerCase().replace(/\s+/g, '_')}_report_${formatDate()}.pdf`;
      pdf.save(filename);
      
      const chartMsg = chartImages.length > 0 ? ` with ${chartImages.length} chart(s)` : '';
      toast.success(`Exported ${insights.length} insights${chartMsg} to PDF`);
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setDownloading(null);
    }
  };

  const exportWord = async () => {
    setDownloading('word');
    try {
      if (!insights || insights.length === 0) {
        toast.error('No insights to export');
        setDownloading(null);
        return;
      }

      const children = [
        new Paragraph({
          text: `${pageTitle} - Insights Report`,
          heading: HeadingLevel.HEADING_1,
        }),
        new Paragraph({
          children: [
            new TextRun({
              text: `Generated: ${formatDateTime()}`,
              italics: true,
              size: 20,
            }),
          ],
        }),
        new Paragraph({ text: '' }),
      ];

      insights.forEach((insight, index) => {
        const severity = (insight.severity || insight.priority || 'info').toUpperCase();
        const title = insight.title || insight.name || `Insight ${index + 1}`;
        const description = insight.description || insight.message || insight.content || '';
        const recommendation = insight.recommendation || insight.action;

        // Title with severity
        children.push(
          new Paragraph({
            children: [
              new TextRun({
                text: `[${severity}] `,
                bold: true,
                color: severity === 'CRITICAL' ? 'DC3545' : 
                       severity === 'HIGH' || severity === 'WARNING' ? 'FFC107' : 
                       severity === 'POSITIVE' ? '28A745' : '6C757D',
              }),
              new TextRun({
                text: title,
                bold: true,
              }),
            ],
          })
        );

        // Description
        if (description) {
          children.push(
            new Paragraph({
              text: description,
            })
          );
        }

        // Recommendation
        if (recommendation) {
          children.push(
            new Paragraph({
              children: [
                new TextRun({
                  text: `→ ${recommendation}`,
                  italics: true,
                  color: '464646',
                }),
              ],
            })
          );
        }

        children.push(new Paragraph({ text: '' }));
      });

      // Footer
      children.push(
        new Paragraph({
          children: [
            new TextRun({
              text: 'Hospice CareLens - Confidential',
              italics: true,
              size: 18,
            }),
          ],
        })
      );

      const doc = new Document({
        sections: [{
          properties: {},
          children: children,
        }],
      });

      const blob = await Packer.toBlob(doc);
      const filename = `${pageTitle.toLowerCase().replace(/\s+/g, '_')}_insights_${formatDate()}.docx`;
      saveAs(blob, filename);
      toast.success(`Exported ${insights.length} insights to Word`);
    } catch (error) {
      console.error('Word export error:', error);
      toast.error('Failed to export Word document');
    } finally {
      setDownloading(null);
    }
  };

  const hasInsights = insights && insights.length > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size} 
          data-testid="export-insights-btn"
          disabled={!hasInsights}
        >
          {downloading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Export Insights
          {hasInsights && <span className="ml-1 text-xs">({insights.length})</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4" />
          Export {insights?.length || 0} Insights
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem 
          onClick={exportPDF}
          disabled={downloading === 'pdf' || !hasInsights}
          className="cursor-pointer"
        >
          <div className="flex items-center gap-2 flex-1">
            <BarChart3 className="h-4 w-4 text-[#00b0ab]" />
            <div className="flex flex-col">
              <span className="font-medium">PDF with Charts</span>
              <span className="text-xs text-muted-foreground">Includes visual analytics</span>
            </div>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={exportCSV}
          disabled={downloading === 'csv' || !hasInsights}
          className="cursor-pointer"
        >
          <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
          Export as CSV
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={exportWord}
          disabled={downloading === 'word' || !hasInsights}
          className="cursor-pointer"
        >
          <File className="h-4 w-4 mr-2 text-blue-600" />
          Export as Word
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default InsightsExport;
